import { Building, Factory, House, ImageOff } from "lucide-react";
import type { ComponentProps } from "react";
import { useState } from "react";

import type { NullableImageUrl } from "~/entities/service";
import { getRandomElement } from "~/shared/arr";
import { EMPTY } from "~/shared/str";
import { cn } from "~/shared/ui";
import { Avatar, AvatarFallback, AvatarImage } from "~/shared/ui/avatar";
import { Skeleton } from "~/shared/ui/skeleton";

type Props = ComponentProps<typeof Avatar> & {
  imageProps: Omit<ComponentProps<typeof AvatarImage>, "src"> & {
    src: NullableImageUrl;
  };
  skeletonProps?: ComponentProps<typeof Skeleton> | undefined;
};

function ServiceImageBlock({
  imageProps: { src, alt: imageAlt, className: imageClassName, ...imageProps },
  skeletonProps: { className: skeletonClassName, ...skeletonProps } = {},
  className,
  ...props
}: Props) {
  const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>("idle");

  const FallbackIcon = typeof src === "string" ? ImageOff : getRandomElement(ICONS)!;
  const fallback = (
    <AvatarFallback className="from-primary to-primary-lighter text-primary-foreground rounded-none bg-linear-to-r">
      <FallbackIcon className="size-16" />
    </AvatarFallback>
  );

  return (
    <Avatar className={cn("flex h-48 w-full rounded-xl", className)} {...props}>
      {typeof src === "string" ? (
        <>
          <AvatarImage
            src={src}
            alt={imageAlt ?? EMPTY}
            className={cn("object-cover", imageClassName)}
            onLoadingStatusChange={setLoadingStatus}
            {...imageProps}
          />
          {(["idle", "loading"] satisfies LoadingStatus[] as string[]).includes(loadingStatus) ? (
            <Skeleton className={cn("w-full", skeletonClassName)} {...skeletonProps} />
          ) : (
            fallback
          )}
        </>
      ) : (
        fallback
      )}
    </Avatar>
  );
}

type LoadingStatus = Parameters<
  NonNullable<ComponentProps<typeof AvatarImage>["onLoadingStatusChange"]>
>[0];

const ICONS = [House, Building, Factory];

export { ServiceImageBlock };
